import type { CSSProperties } from 'vue';
import { ApplicationDefaults } from './ApplicationDefaults';
import { ClientConstants } from './ClientConstants';
import type { INumberSuffix } from '@amcharts/amcharts5';

const height = 400;

const VITE_CLIENT = import.meta.env.VITE_CLIENT;

const defaultBenchmarksByCompany: {
  [client: string]: { BENCHMARK_1: string; BENCHMARK_2: string; BENCHMARK_3: string };
} = {
  [ClientConstants.PREMIALAB]: {
    BENCHMARK_1: 'SPX',
    BENCHMARK_2: 'ITRROV',
    BENCHMARK_3: 'SPTR500N',
  },
  [ClientConstants.BARCLAYS]: {
    BENCHMARK_1: 'SPX',
    BENCHMARK_2: 'BXIIMMEE',
    BENCHMARK_3: 'SPTR500N',
  },
  [ClientConstants.BOFA]: {
    BENCHMARK_1: 'MSDUWI',
    BENCHMARK_2: 'MSDUUS',
    BENCHMARK_3: 'MSDEE15N',
  },
};

const ChartDefaults: {
  HEIGHT: number;
  PERF_ATTRIB_PDF_CHART_HEIGHT: number;
  TITLE_TAG: string;
  STROKE_WIDTH: number;
  COMPARISON_SERIES_STROKE_WIDTH: number;
  LABEL_FONT_WEIGHT:
    | 'bold'
    | '500'
    | 'normal'
    | 'bolder'
    | 'lighter'
    | '100'
    | '200'
    | '300'
    | '400'
    | '600'
    | '700'
    | '800'
    | '900'
    | undefined;
  DATE_FORMAT: string;
  HEIGHT_STYLE: CSSProperties;
  /**
   * Charts must account for axis labels. In order to center the last axis label on the last axis tick, the chart MUST
   * have some padding on the right. But this mandatory padding then affects our charts such that the right side of the
   * chart does not align with the right hand side of other content on the page. So we add a negative margin to all
   * these charts. But if we do that, then we must force the paddingRight on those charts to be exactly this value.
   * This way, the inside area of the chart will always match the boundaries of the other content on the page.
   */
  DEFAULT_SPACING_RIGHT: number;

  PADDING_LEFT: number;
  PADDING_TOP: number;

  AXIS_LABEL_FONT_SIZE: number;
  AXIS_LABEL_PDF_FONT_SIZE: number;

  DEFAULT_COLORS: {
    /**
     * When we overwrite our app colors with whitelabel colors,
     * we may want this primary color in the charts to actually be separate
     * from the primary brand color.
     */
    primary: string;
    info: string;
    /**
     * Analytics / factsheet benchmark series and labels
     */
    benchmark: string;
    /**
     * In some cultures, red may not be the appropriate color to show
     * for a negative return.
     */
    negative: string;
    darkGrey: string;
    grey: string;
    activeReturn: string;
    bodyColor: string;
    labDefault: string;
    tooltipBackgroundColor: string;
    longColor: string;
    shortColor: string;
  };
  DEFAULT_BENCHMARKS: { BENCHMARK_1: string; BENCHMARK_2: string; BENCHMARK_3: string };
  DEFAULT_BIG_NUMBER_SUFFIX: INumberSuffix[];
} = {
  HEIGHT: height,
  PERF_ATTRIB_PDF_CHART_HEIGHT: 350,
  TITLE_TAG: 'h5',
  STROKE_WIDTH: 2,
  COMPARISON_SERIES_STROKE_WIDTH: 3,
  LABEL_FONT_WEIGHT: '500',
  DATE_FORMAT: 'yyyy-MM-dd',
  HEIGHT_STYLE: { height: `${height}px` },
  DEFAULT_SPACING_RIGHT: 15,

  PADDING_LEFT: 20,
  PADDING_TOP: 16,

  AXIS_LABEL_FONT_SIZE: 12,
  AXIS_LABEL_PDF_FONT_SIZE: 14,

  DEFAULT_COLORS: {
    /**
     * When we overwrite our app colors with whitelabel colors,
     * we may want this primary color in the charts to actually be separate
     * from the primary brand color.
     */
    primary: ApplicationDefaults.colors.Primary,
    info: ApplicationDefaults.colors.Info,
    benchmark: ApplicationDefaults.colors.Benchmark,
    /**
     * In some cultures, red may not be the appropriate color to show
     * for a negative return.
     */
    negative: ApplicationDefaults.colors.NegativeReturn,
    /**
     * To improve maintainability and align with the design system, we aim to eliminate hardcoded color codes and inconsistent naming conventions for gray shades.
     * For example, instead of using `darkGrey: '#999999'` or `grey: '#A9A9A9'`, please uses `ApplicationDefaults.colors.Gray100`.
     * For more details and progress on the design system migration, refer to: https://premialab.atlassian.net/browse/WAA-11848
     */
    darkGrey: '#999999',
    grey: '#A9A9A9',
    bodyColor: ApplicationDefaults.colors.BodyGray,
    labDefault: ApplicationDefaults.colors.LabDefault,
    activeReturn: ApplicationDefaults.colors.ActiveReturn,
    tooltipBackgroundColor: ApplicationDefaults.colors.Gray100,
    /**
     * This color was manually extracted from the color dropper tool when the series fill was ApplicationDefaults.colors.Info
     * and fillOpacity set to 0.5. This was removed so that we could match the color exactly for the tooltip bullet color.
     *
     * TODO: WAA-11848: this should be integrated into the design system as something like teal-300.
     */
    longColor: '#8cc2d0',
    /**
     * This color was manually extracted from the color dropper tool when the series fill was ApplicationDefaults.colors.NegativeReturn
     * and fillOpacity set to 0.5. This was removed so that we could match the color exactly for the tooltip bullet color.
     *
     * TODO: WAA-11848: this should be integrated into the design system as something like red-300.
     */
    shortColor: '#cb9ead',
  },
  DEFAULT_BENCHMARKS: defaultBenchmarksByCompany[VITE_CLIENT] ?? defaultBenchmarksByCompany[ClientConstants.PREMIALAB],
  /**
   * The array should be sorted by key: number
   */
  DEFAULT_BIG_NUMBER_SUFFIX: [
    { number: 1e3, suffix: 'K' },
    { number: 1e6, suffix: 'M' },
    { number: 1e9, suffix: 'B' },
    { number: 1e12, suffix: 'T' },
  ],
};

export default ChartDefaults;
